import {isEmpty} from 'lodash';
import {stringify} from 'query-string';
import Env from './env';

export const HTTP_REQUEST_METHODS = {
	// The GET method requests a representation of the specified resource. Requests using GET should only retrieve data.
	GET: 'GET',
	// The HEAD method asks for a response identical to that of a GET request, but without the response body.
	HEAD: 'HEAD',
	// The POST method is used to submit an entity to the specified resource, often causing a change in state or side effects on the server.
	POST: 'POST',
	// The PUT method replaces all current representations of the target resource with the request payload.
	PUT: 'PUT',
	// The DELETE method deletes the specified resource.
	DELETE: 'DELETE',
	// The CONNECT method establishes a tunnel to the server identified by the target resource.
	CONNECT: 'CONNECT',
	// The OPTIONS method is used to describe the communication options for the target resource.
	OPTIONS: 'OPTIONS',
	// The TRACE method performs a message loop-back test along the path to the target resource.
	TRACE: 'TRACE',
	// The PATCH method is used to apply partial modifications to a resource.
	PATCH: 'PATCH'
};

const ERRORS = {
	NO_RESOURCE_SPECIFIED: 'No resource was specified.'
};

const arrayFormat = 'bracket-separator';
const arrayFormatSeparator = '|';

export const API_RESOURCES = {
	// Add the corresponding available Api resources here
	V2: 'v2',
	ENGAGEMENTS: 'Engagements',
	FORMS: {
		FORMS: 'forms',
		SNAPSHOTS: 'snapshots',
		HEADERS: 'headers',
		SECTIONS: 'sections',
		BODIES: 'bodies',
		SECTION: 'section',
		FORMPROFILE: 'FormProfile'
	},
	FORMACTIONS: 'formactions',
	FORMBODIES: 'formbodies',
	ITAPPLICATIONS: 'itapplications',
	SUPPORTINGITAPPLICATIONS: 'supportingITApplications',
	ITPROCESSES: 'itprocess',
	ITATTRIBUTES: 'itattributes',
	RISKFACTORS: {
		RISKFACTORS: 'riskfactors',
		RELATIONS: 'relation'
	},
	SCOTS: 'scots',
	SCOT: 'scot',
	USERS: 'users',
	ITRISK: 'ITRisk',
	ITRISKFACTOR: 'ITRiskFactor',
	CONTROL: 'Controls',
	ITCONTROLS: 'ITControls',
	TASKS: 'tasks',
	TASKOVERVIEW: 'taskOverview',
	ITPROCESS: 'itprocesses',
	RISKTYPES: 'risktypes',
	RISKS: 'risks',
	TIMEPHASES: 'TimePhases',
	HANDOFFS: 'HandOffs',
	DOCUMENTS: 'Documents',
	ASSIGNMENTS: 'Assignments',
	ACCOUNTS: 'Accounts',
	ACCOUNTMETRICS: 'accountmetrics',
	HELIX_PROJECTS: 'HelixProjects',
	DIAGNOSTICS: 'Diagnostics',
	TELEMETRY: 'Telemetry',
	TRIAL_BALANCE: 'TrialBalances',
	ESTIMATE: 'Estimates',
	COMPONENTS: 'GroupComponents',
	INSTRUCTIONS: 'GroupInstructions',
	SCOPES: 'GroupScopes',
	FORMBODYOPTION: 'FormBodyOption',
	FORMBODYOPTIONS: 'formbodyoptions',
	GROUPLINKS: 'GroupLinks',
	MULTIENTITY: 'multiEntity',
	GROUPSECTIONS: 'GroupSections',
	GROUPSNAPSHOTS: 'GroupSnapshots',
	ACCOUNT: 'Account',
	RISKASSESSMENTPACKAGE: 'RiskAssessmentPackages',
	COMPARE: 'Compare',
	PROFILE: 'Profile',
	GROUPTASKS: 'GroupTasks',
	INVOLVEMENTPACKAGES: 'InvolvementPackages',
	LINKEDFORMS: 'LinkedForms',
	KNOWLEDGEFORMTYPES: 'KnowledgeFormTypes',
	DOCUMENTIMPORT: 'documentimports.json',
	DOCUMENTSJSON: 'documents.json',
	CONTENTS: 'Contents',
	FLOWCHART: 'Flowcharts',
	SAMPLELISTS: 'sampleLists',
	SAMPLEITEMS: 'sampleItems',
	FLOWCHARTSTEPS: 'FlowchartSteps',
	IT_APPLICATIONS: 'ITApplications',
	TARGETSTEPS: 'TargetSteps',
	CONTROL_ATTRIBUTES: 'Attributes',
	PARENT_RISKS: 'ParentRisks',
	WCGWS: 'WCGWs',
	CONTROLS: 'Controls',
	TAGS: 'Tags',
	TAGGROUPS: 'TagGroups',
	ENTITIES: 'Entities',
	ASSERTION: 'Assertion',
	ASSERTIONS: 'Assertions',
	GROUPCOMPONENTS: 'GroupComponents',
	DOCUMENT_TYPES: 'DocumentTypes',
	JUDGEMENT_TYPES: 'JudgementTypes',
	DEFICIENCY: 'deficiencies',
	LISTITAPPLICATIONS: 'listitapplications',
	MULTIENTITIES: 'multientities',
	ITAPPLICATIONMETRICS: 'itapplicationmetrics',
	RISK_METRICS: 'riskmetrics',
	SCOT_METRICS: 'scotmetrics',
	KNOWLEDGE_SCOT_INDEXES: 'knowledgescotindexes',
	LISTITRISKS: 'listitrisks',
	ITCONTROL: 'ItControl'
};

/**
 * Builds an Api request Url based on the specified parameters.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} queryString An object holding the Url's query string parameters.
 * @param {string} arrayFormat The format to use for array query parameters.
 * @returns {string} The Url built using the specified object parameters.
 */
export function getUrl(resource, queryString = undefined, arrayFormat) {
	let url = Env.getServerUrl();

	if (resource !== undefined && !isEmpty(resource)) {
		url += Object.entries(resource)
			.map((item) => {
				let path = item[0];
				if (item[1] !== undefined && item[1] !== null) {
					path += `/${item[1]}`;
				}
				return path;
			})
			.join('/');
	} else {
		throw ERRORS.NO_RESOURCE_SPECIFIED;
	}

	let _queryString;
	if (queryString) {
		_queryString = stringify(queryString, {
			arrayFormat,
			arrayFormatSeparator,
			skipNull: true
		});
		url += `?${_queryString}`;
	}
	return url;
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} data An optional payload to send to the server if necessary.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @param {string} trackWithArea area string
 * @returns {object} The new action object.
 */
export function createApiCreateAction(type, resource, data, args = {}, isOnline = true, trackWithArea = undefined) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource),
				method: HTTP_REQUEST_METHODS.POST,
				data,
				trackWithArea: trackWithArea
			}
		},
		isOnline
	};
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} queryString An object holding the Url's query string parameters.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @param {string} trackWithArea area string
 * @param {string} arrayFormat The format to use for array query parameters.
 * @param {*} payload An optional payload to include in the action object.
 * @returns {object} The new action object.
 */
export function createApiReadAction(
	type,
	resource,
	queryString = undefined,
	args = {},
	isOnline = true,
	trackWithArea = undefined,
	arrayFormat = arrayFormat,
	payload
) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource, queryString, arrayFormat),
				method: HTTP_REQUEST_METHODS.GET,
				trackWithArea: trackWithArea
			}
		},
		isOnline,
		payload
	};
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} data An optional payload to send to the server if necessary.
 * @param {object} method The Http method to be used, defaults to PUT.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @param {string} trackWithArea area string
 * @returns {object} The new action object.
 */
export function createApiUpdateAction(
	type,
	resource,
	data = undefined,
	method = HTTP_REQUEST_METHODS.PUT,
	args = {},
	isOnline = true,
	trackWithArea = undefined
) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource),
				method,
				data,
				trackWithArea: trackWithArea
			}
		},
		isOnline
	};
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @param {string} trackWithArea area string
 * @param {object} queryString An object holding the Url's query string parameters.
 * @param {object} data An optional payload to send to the server if necessary.
 * @returns {object} The new action object.
 */
export function createApiDeleteAction(
	type,
	resource,
	args = {},
	isOnline = true,
	trackWithArea = undefined,
	queryString = undefined,
	data = undefined
) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource, queryString),
				method: HTTP_REQUEST_METHODS.DELETE,
				data,
				trackWithArea: trackWithArea
			}
		},
		isOnline
	};
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously using PATCH.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} data An optional payload to send to the server if necessary.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @param {string} trackWithArea area string
 * @returns {object} The new action object.
 */
export function createApiPatchAction(
	type,
	resource,
	data = undefined,
	args = {},
	isOnline = true,
	trackWithArea = undefined
) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource),
				method: HTTP_REQUEST_METHODS.PATCH,
				data,
				trackWithArea: trackWithArea
			}
		},
		isOnline
	};
}

/**
 * Creates an action with the specified parameters that will be handled by the api-middleware asynchronously.
 * @param {string} type The type of the created action.
 * @param {object} resource An object holding the Url's resource part information.
 * @param {object} data An optional payload to send to the server if necessary.
 * @param {object} args An optional object with arguments to be available in the action object.
 * @param {boolean} isOnline Specifies whether the application has Api connectivity, defaults to true.
 * @param {string} trackWithArea area string
 * @returns {object} The new action object.
 */
export function getByIdApiReadAction(
	type,
	resource,
	data = undefined,
	args = {},
	isOnline = true,
	trackWithArea = undefined
) {
	return {
		type,
		...args,
		meta: {
			api: {
				url: getUrl(resource, data),
				method: HTTP_REQUEST_METHODS.GET,
				data,
				trackWithArea: trackWithArea
			}
		},
		isOnline
	};
}
